import { HttpResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BookingChat } from '../booking-chat';
import { BookingClientFile } from '../booking-client-file';
import { BookingClientFileOpen } from '../booking-client-file-open';
import { BookingFileOrigin } from '../booking-file-origin.enum';
import { CKEDITORCONFIG_BOOKING_CONFIRMATION } from '../ckeditor-config';
import { HttpService } from '../service/http.service';
import { LoginService } from '../security/login.service';
import { EASYLANGUAGES_LOGO, LANGUESVIVANTES_LOGO } from '../logo-src-constants';
import { OpenBookingFile } from '../open-booking-file';
import { OpenBookingInformation } from '../open-booking-information';
import { OpenGlobalFile } from '../open-global-file';
import { OpenSchoolFile } from '../open-school-file';
import { ToastMessageService } from '../toast-message.service';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-dashboard-general-client',
  templateUrl: './dashboard-general-client.component.html',
  styleUrls: ['./dashboard-general-client.component.css']
})
export class DashboardGeneralClientComponent implements OnInit {

  uuid!: string;
  sbmsBaseUrl = environment.sbmsBaseUrl;
  bookingClientFiles: BookingClientFileOpen[] = [];
  stepItems: MenuItem[] = [];
  activeIndex!: number;
  uploadedClientFiles: BookingClientFile[] = [];
  chatMessage!: string;
  remainingCharacters: number = 255;
  recaptchaResponse!: string;
  bookingChats: BookingChat[] = [];
  editorConfig = CKEDITORCONFIG_BOOKING_CONFIRMATION;
  maxTextLength = 10000;
  bookingInformation!: OpenBookingInformation;
  displaySchoolInformation = false;
  displayAccommodationInformation = false;
  displaySchoolEmergencyPhoneNumber = false;

  schoolFilesToComplete: OpenSchoolFile[] = [];
  globalFilesToComplete: OpenGlobalFile[] = [];
  openBookingFiles: OpenBookingFile[] = [];
  openBookingFilesToComplete: OpenBookingFile[] = [];
  lviLogo!: string;
  translatedLanguageCourse!: string;
  lviSiteUrl = "https://languesvivantes.com";

  @ViewChild("captcha") recaptcha: any;

  constructor(
    private route: ActivatedRoute,
    private httpService: HttpService,
    private toastService: ToastMessageService,
    @Inject(LOCALE_ID)
    public localeId: string,
    private loginService: LoginService
  ) { }

  ngOnInit(): void {
    if (this.localeId == "fr") {
      this.lviLogo = LANGUESVIVANTES_LOGO;
    } else {
      this.lviLogo = EASYLANGUAGES_LOGO;
    }
    this.loginService.activatedNavigationComponent = false;
    this.uuid = this.route.snapshot.paramMap.get("uuid")!;
    this.lviSiteUrl = this.lviSiteUrl + "/" + this.localeId;
    this.updateBookingFilesToComplete();
    this.updateOpenBookingFiles();
    this.updateBookingChats();
    this.httpService.getOpenBookingInformationByUuid(this.uuid, this.localeId).subscribe(res => {
      this.bookingInformation = res;
      this.translatedLanguageCourse = this.getTranslatedLanguageCourseName()!;
    });
    forkJoin({
      statuses: this.httpService.getPublicBookingStatuses(this.localeId),
      selectedStatus: this.httpService.getBookingStatusByUuid(this.uuid, this.localeId)
    }).subscribe(({ statuses, selectedStatus }) => {
      for (let i = 0; i < statuses.length; i++) {
        this.stepItems.push({ label: statuses[i].name });
        if (statuses[i].id == selectedStatus.id) {
          this.activeIndex = i;
        }
      }
    });
  }

  updateBookingChats() {
    this.bookingChats = [];
    this.httpService.getBookingChats(this.uuid).subscribe(res => {
      this.bookingChats = res;
    });
  }

  updateClientFiles() {
    this.httpService.getBookingClientFilesByUuid(this.uuid).subscribe(res => {
      this.bookingClientFiles = res;
    });
  }

  updateGlobalFiles() {
    this.httpService.getOpenGlobalFilesToCompleteByUuid(this.uuid).subscribe(res => {
      this.globalFilesToComplete = res;
    });
  }

  updateBookingFilesToComplete() {
    this.httpService.getOpenBookingFilesToCompleteByUuid(this.uuid).subscribe(res => {
      this.openBookingFilesToComplete = res;
    });
  }

  updateSchoolFiles() {
    this.httpService.getOpenSchoolFilesToCompleteByUuid(this.uuid).subscribe(res => {
      this.schoolFilesToComplete = res;
    });
  }

  updateOpenBookingFiles() {
    this.httpService.getOpenBookingFilesByUuid(this.uuid).subscribe(res => {
      this.openBookingFiles = res;
    });
  }

  globalFileUpload(event: any, fileForm: any, originalFile: OpenGlobalFile) {
    if (event.files.length == 0) {
      return;
    }
    let file = event.files[0];

    const formData = new FormData;
    formData.append("file", file, file.name);

    this.httpService.postBookingGlobalFileResponse(this.uuid, formData, originalFile.id).subscribe(() => {
      this.toastService.addSuccess("Success", "File uploaded");
      fileForm.clear();
      originalFile.isCompleted = true;
    }, error => {
      this.toastService.addError("Error", error.error.message);
      console.log(error);
    });
  }

  uploadCompletedFile(event: any, fileForm: any, originalFile: OpenBookingFile) {
    switch (originalFile.fileOrigin) {
      case BookingFileOrigin.GLOBAL_FILE:
        this.globalFileUpload(event, fileForm, originalFile);
        break;
      case BookingFileOrigin.SCHOOL_FILE:
        this.schoolFileUpload(event, fileForm, originalFile);
        break;
      case BookingFileOrigin.INDIVIDUAL_FILE:
        this.individualFileUpload(event, fileForm, originalFile);
        break;
    }
  }

  schoolFileUpload(event: any, fileForm: any, originalFile: OpenSchoolFile) {
    if (event.files.length == 0) {
      return;
    }
    let file = event.files[0];

    const formData = new FormData;
    formData.append("file", file, file.name);

    this.httpService.postBookingSchoolFileResponse(this.uuid, formData, originalFile.id).subscribe(() => {
      this.toastService.addSuccess("Success", "File uploaded");
      fileForm.clear();
      originalFile.isCompleted = true;
    }, error => {
      this.toastService.addError("Error", error.error.message);
      console.log(error);
    });
  }

  individualFileUpload(event: any, fileForm: any, originalFile: OpenBookingFile) {
    if (event.files.length == 0) {
      return;
    }
    let file = event.files[0];

    const formData = new FormData;
    formData.append("file", file, file.name);

    this.httpService.postBookingIndividualFileResponse(this.uuid, formData, originalFile.id).subscribe(() => {
      this.toastService.addSuccess("Success", "File uploaded");
      fileForm.clear();
      originalFile.isCompleted = true;
    }, error => {
      this.toastService.addError("Error", error.error.message);
      console.log(error);
    });
  }

  downloadBookingFile(file: OpenBookingFile) {
    switch (file.fileOrigin) {
      case BookingFileOrigin.GLOBAL_FILE:
        this.httpService.getPublicBookingGlobalFileById(file.id, this.uuid).subscribe(res => {
          this.downloadOpenBookingFile(file, res.body!);
        });
        break;
      case BookingFileOrigin.SCHOOL_FILE:
        this.httpService.getPublicBookingSchoolFileById(file.id, this.uuid).subscribe(res => {
          this.downloadOpenBookingFile(file, res.body!);
        });
        break;
      case BookingFileOrigin.INDIVIDUAL_FILE:
        this.httpService.getIndividualBookingFileById(file.id, this.uuid).subscribe(res => {
          this.downloadOpenBookingFile(file, res);
        });
    }
  }

  downloadOpenBookingFile(file: OpenBookingFile, fileBlob: Blob) {
    let binaryData = [];
    binaryData.push(fileBlob);
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
    downloadLink.setAttribute('download', file.fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  downloadSchoolFile(fileId: number, fileName: string) {
    this.httpService.getPublicBookingSchoolFileById(fileId, this.uuid).subscribe(res => {
      let binaryData = [];
      binaryData.push(res.body!);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  updateRecaptchaResponse(event: any) {
    if (event.response) {
      this.recaptchaResponse = event.response;
    }
  }

  activateSchoolInformationDialog() {
    this.displaySchoolInformation = true;
  }

  activateAccommodationInformationDialog() {
    this.displayAccommodationInformation = true;
  }
  activateSchoolEmergencyPhoneNumberDialog() {
    this.displaySchoolEmergencyPhoneNumber = true;
  }

  getTranslatedLanguageCourseName() {
    if (this.bookingInformation.languageCourse == null) {
      return null;
    }
    //If the description in selected language is null, return English description
    switch (this.localeId) {
      case "fr":
        if(this.bookingInformation.languageCourse.descriptionFr == null){
          return this.bookingInformation.languageCourse.descriptionEn;
        }
        return this.bookingInformation.languageCourse.descriptionFr;
      case "en":
        return this.bookingInformation.languageCourse.descriptionEn;
      case "nl":
        if(this.bookingInformation.languageCourse.descriptionNl == null){
          return this.bookingInformation.languageCourse.descriptionEn;
        }
        return this.bookingInformation.languageCourse.descriptionNl;
      case "es":
        if(this.bookingInformation.languageCourse.descriptionEs == null){
          return this.bookingInformation.languageCourse.descriptionEn;
        }
        return this.bookingInformation.languageCourse.descriptionEs;
      case "pt":
        if(this.bookingInformation.languageCourse.descriptionPt == null){
          return this.bookingInformation.languageCourse.descriptionEn;
        }
        return this.bookingInformation.languageCourse.descriptionPt;
      default:
        return null;
    }
  }
}
